import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ApiError } from '@/api/client';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Switch the active organisation.
 *
 * Switching clears the entire query cache (see AuthProvider.resetCache). That
 * is deliberate and load-bearing: without it, customers, quotations and
 * pricing cached for the previous organisation would remain on screen under
 * the new one's name.
 */
export function OrgSwitcher() {
  const { memberships, activeOrganization, switchOrganization } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nothing to switch between; render the name as plain text.
  if (memberships.length <= 1) {
    return activeOrganization ? (
      <Typography variant="body2" fontWeight={600} noWrap>
        {activeOrganization.name}
      </Typography>
    ) : null;
  }

  const handleSelect = async (organizationId: string) => {
    setAnchorEl(null);

    if (organizationId === activeOrganization?.id) return;

    setSwitching(true);
    setError(null);

    try {
      await switchOrganization(organizationId);
    } catch (e) {
      // Previously swallowed by `void`, which meant a failed switch looked
      // exactly like a working one that changed nothing — the hardest kind of
      // bug to report.
      setError(
        e instanceof ApiError
          ? (e.detail ?? e.title)
          : 'Could not switch organisation. Please try again.',
      );
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        color="inherit"
        disabled={switching}
        endIcon={switching ? <CircularProgress size={14} /> : <ExpandMoreIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ maxWidth: 260 }}
      >
        <Typography variant="body2" fontWeight={600} noWrap>
          {activeOrganization?.name ?? 'Select organisation'}
        </Typography>
      </Button>

      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {memberships.map((membership) => {
          const isActive = membership.organization.id === activeOrganization?.id;

          return (
            <MenuItem
              key={membership.id}
              selected={isActive}
              onClick={() => void handleSelect(membership.organization.id)}
            >
              <ListItemIcon>{isActive && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText
                primary={membership.organization.name}
                secondary={membership.role_label}
              />
              <Chip
                size="small"
                variant="outlined"
                label={membership.organization.default_currency}
                sx={{ ml: 2 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
